import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import CountrySelect from "./CountrySelect";

const GENDERS = ["kvinne", "mann", "par", "trans", "annet"];

export default function OnboardingModal() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [locations, setLocations] = useState({ countries: [], top_countries: ["NO","SE","DK","GB","US"], subdivisions: {}, fylker: [] });
  const [step, setStep] = useState(1);
  const [country, setCountry] = useState("NO");  // ISO 3166-1 alpha-2
  const [fylke, setFylke] = useState("");
  const [city, setCity] = useState("");
  const [gender, setGender] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAgeVerify, setShowAgeVerify] = useState(false);   // Ekstra step 4 etter submit
  const [ageVerifyStarting, setAgeVerifyStarting] = useState(false);

  useEffect(() => {
    if (user && user !== false && user.is_onboarded === false) setOpen(true);
    else setOpen(false);
  }, [user]);

  useEffect(() => {
    api.get("/search/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);

  // Reset fylke når land endres
  useEffect(() => { setFylke(""); }, [country]);

  const activeSubdivisions = useMemo(() => {
    if (country && locations.subdivisions?.[country]) return locations.subdivisions[country];
    if (country === "NO") return locations.fylker || [];
    return [];
  }, [country, locations]);

  if (!open) return null;

  const fylkeLabel = country === "SE" ? t("country_select.fylke_se")
    : country === "DK" ? t("country_select.fylke_dk")
    : country === "NO" ? t("country_select.fylke_no")
    : t("country_select.fylke_other");

  const next = () => {
    setError("");
    if (step === 1 && (!country || !city.trim())) return setError(t("onboarding.err_country_city"));
    if (step === 2 && !gender) return setError(t("onboarding.err_gender"));
    if (step === 3 && (!year || !month || !day)) return setError(t("onboarding.err_birth"));
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setError(""); setLoading(true);
    try {
      await api.post("/users/me/onboarding", {
        country, fylke: fylke || null, city: city.trim(), gender,
        birth_year: parseInt(year), birth_month: parseInt(month), birth_day: parseInt(day),
      });
      await refresh();
      // Sjekk BankID-status — hvis ikke verifisert, vis alders-verif-prompt
      try {
        const st = await api.get("/me/bankid/status");
        if (st.data && !st.data.glimt_eligible) {
          setShowAgeVerify(true);
          return;   // Ikke lukk modal enda
        }
      } catch { /* ignorer — lukk modalen som normalt */ }
      setOpen(false);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const startAgeVerify = async () => {
    setAgeVerifyStarting(true);
    try {
      const r = await api.post("/me/bankid/start", { next_url: "/" });
      if (r.data.already_verified) {
        setShowAgeVerify(false);
        setOpen(false);
      } else if (r.data.redirect_url) {
        window.location.href = r.data.redirect_url;
      }
    } catch (e) {
      setError(formatApiError(e));
      setAgeVerifyStarting(false);
    }
  };

  const skipAgeVerify = () => {
    setShowAgeVerify(false);
    setOpen(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />
      <div className="relative max-w-lg w-full editorial-card p-8 sm:p-10 animate-fade-up" data-testid="onboarding-modal">
        {showAgeVerify ? (
          <>
            <div className="font-meta mb-3">SISTE STEG</div>
            <h2 className="font-fraunces italic text-3xl text-[var(--cream)] leading-tight mb-2">
              Verifiser alder (18+)
            </h2>
            <p className="font-manrope text-sm text-[var(--cream-soft)] mb-6">
              Norsk Erotikk er 18+. For å låse opp Glimt og andre voksen-funksjoner må du bekrefte alderen din — enten via BankID (rask, gratis for deg) eller ved å kjøpe én token-pakke via kort/Strex (som også regnes som alders-verifisering).
            </p>
            <div className="space-y-3 mb-6">
              <div className="p-4 border border-[var(--magenta)]/30 bg-[var(--magenta)]/5">
                <p className="font-fraunces text-[var(--magenta)] mb-1 text-sm">Alternativ 1 — BankID</p>
                <p className="text-[var(--cream-soft)] text-xs">
                  Vi lagrer ikke fødselsnummer, kun navn + verifisert alder. Kostnaden dekkes av oss.
                </p>
              </div>
              <div className="p-4 border border-[var(--gold)]/30 bg-[var(--gold)]/5">
                <p className="font-fraunces text-[var(--gold)] mb-1 text-sm">Alternativ 2 — Kjøp tokens senere</p>
                <p className="text-[var(--cream-soft)] text-xs">
                  Én token-pakke via kort eller Strex teller også (Strex krever 18+). Kan gjøres når som helst.
                </p>
              </div>
            </div>
            {error && <div className="text-sm text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3 mb-4">{error}</div>}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={skipAgeVerify}
                className="btn-ghost"
                data-testid="onboarding-age-skip"
              >
                Gjør det senere
              </button>
              <button
                type="button"
                onClick={startAgeVerify}
                disabled={ageVerifyStarting}
                className="btn-magenta flex-1 inline-flex items-center justify-center gap-2"
                data-testid="onboarding-age-verify-start"
              >
                {ageVerifyStarting
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <ShieldCheck className="h-4 w-4" />}
                {ageVerifyStarting ? "Starter BankID…" : "Verifiser nå med BankID"}
              </button>
            </div>
          </>
        ) : (
          <>
        <div className="font-meta mb-3">{t("onboarding.step", { n: step })}</div>
        <h2 className="font-fraunces italic text-3xl text-[var(--cream)] leading-tight mb-2">
          {step === 1 && t("onboarding.t_where")}
          {step === 2 && t("onboarding.t_who")}
          {step === 3 && t("onboarding.t_born")}
        </h2>
        <p className="font-manrope text-sm text-[var(--cream-soft)] mb-8">
          {step === 1 && t("onboarding.d_where")}
          {step === 2 && t("onboarding.d_who")}
          {step === 3 && t("onboarding.d_born")}
        </p>

        {step === 1 && (
          <div className="space-y-5">
            <div>
              <div className="font-meta mb-2">{t("onboarding.country")}</div>
              <CountrySelect
                value={country}
                onChange={(iso) => setCountry(iso || "NO")}
                allCountries={locations.countries || []}
                topCountries={locations.top_countries || ["NO","SE","DK","GB","US"]}
                allowEmpty={false}
                testIdPrefix="onboarding-country"
              />
            </div>
            {activeSubdivisions.length > 0 && (
              <div>
                <div className="font-meta mb-2">{fylkeLabel}</div>
                <select value={fylke} onChange={(e)=>setFylke(e.target.value)} className="input-editorial" data-testid="onboarding-fylke">
                  <option value="">{t("onboarding.pick_fylke")}</option>
                  {activeSubdivisions.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            )}
            <div>
              <div className="font-meta mb-2">{t("onboarding.city")}</div>
              <input value={city} onChange={(e)=>setCity(e.target.value)} placeholder={t("onboarding.city_ph")}
                     className="input-editorial" data-testid="onboarding-city" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="font-meta mb-3">{t("onboarding.gender_label")}</div>
            <div className="grid grid-cols-2 gap-2">
              {GENDERS.map((k) => (
                <button key={k} onClick={()=>setGender(k)} type="button"
                        className={`p-4 border text-left font-fraunces italic text-lg transition-colors ${
                          gender === k
                            ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/5"
                            : "border-white/10 text-[var(--cream)] hover:border-white/30"
                        }`}
                        data-testid={`onboarding-gender-${k}`}>
                  {t(`onboarding.g_${k}`)}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="font-meta mb-2">{t("onboarding.day")}</div>
                <select value={day} onChange={(e)=>setDay(e.target.value)} className="input-editorial">
                  <option value="">—</option>
                  {Array.from({length:31},(_,i)=>i+1).map((d)=> <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <div className="font-meta mb-2">{t("onboarding.month")}</div>
                <select value={month} onChange={(e)=>setMonth(e.target.value)} className="input-editorial">
                  <option value="">—</option>
                  {Array.from({length:12},(_,i)=>i+1).map((m)=> <option key={m} value={m}>{t(`onboarding.m_${m}`)}</option>)}
                </select>
              </div>
              <div>
                <div className="font-meta mb-2">{t("onboarding.year")}</div>
                <select value={year} onChange={(e)=>setYear(e.target.value)} className="input-editorial">
                  <option value="">—</option>
                  {Array.from({length:80},(_,i)=>2008-i).map((y)=> <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <p className="font-manrope text-[11px] text-[var(--cream-faint)] uppercase tracking-[0.2em]">
              {t("onboarding.must_18")}
            </p>
          </div>
        )}

        {error && <div className="text-sm text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3 mt-5">{error}</div>}

        <div className="flex gap-3 mt-10">
          {step > 1 && (
            <button type="button" onClick={()=>setStep((s)=>s-1)} className="btn-ghost">{t("onboarding.back")}</button>
          )}
          {step < 3 ? (
            <button type="button" onClick={next} className="btn-magenta flex-1" data-testid="onboarding-next">
              {t("onboarding.next")}
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={loading} className="btn-magenta flex-1" data-testid="onboarding-submit">
              {loading ? t("onboarding.saving") : t("onboarding.finish")}
            </button>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
}
